const POSITION_BYTES = 12;
const SCAN_INDEX_BYTES = 2;

/**
 * Encodes points as a binary PLY file, readable by CloudCompare, MeshLab or a few lines of code
 *
 * @param positions Point positions as x, y, z triplets
 * @param comments Header comments, e.g. metadata as JSON, one line each
 * @param scanIndices Optional scan index of each point, written as the "scan" property
 * @returns the PLY file
 */
export function encodePly(
  positions: Float32Array,
  comments: string[],
  scanIndices?: Uint16Array,
): Blob {
  const count = positions.length / 3;
  const header = [
    "ply",
    "format binary_little_endian 1.0",
    ...comments.map((comment) => `comment ${comment.replaceAll("\n", " ")}`),
    `element vertex ${count}`,
    "property float x",
    "property float y",
    "property float z",
    ...(scanIndices ? ["property ushort scan"] : []),
    "end_header",
    "",
  ].join("\n");
  const stride = POSITION_BYTES + (scanIndices ? SCAN_INDEX_BYTES : 0);
  const body = new DataView(new ArrayBuffer(count * stride));
  for (let i = 0; i < count; i++) {
    for (let axis = 0; axis < 3; axis++) {
      body.setFloat32(i * stride + axis * 4, positions[i * 3 + axis], true);
    }
    if (scanIndices) {
      body.setUint16(i * stride + POSITION_BYTES, scanIndices[i], true);
    }
  }
  return new Blob([header, body], { type: "application/octet-stream" });
}

const END_OF_HEADER = "end_header\n";

/**
 * @param buffer A file written by encodePly
 * @returns the point positions, the scan indices if any, and the header comments
 */
export function decodePly(buffer: ArrayBuffer): {
  positions: Float32Array;
  scanIndices?: Uint16Array;
  comments: string[];
} {
  const bytes = new Uint8Array(buffer);
  const marker = new TextEncoder().encode(END_OF_HEADER);
  let headerLength = -1;
  for (let i = 0; i <= bytes.length - marker.length && headerLength < 0; i++) {
    if (marker.every((byte, j) => bytes[i + j] === byte)) {
      headerLength = i + marker.length;
    }
  }
  if (headerLength < 0) throw new Error("Not a PLY file");
  const lines = new TextDecoder()
    .decode(bytes.subarray(0, headerLength))
    .split("\n");
  const comments = lines
    .filter((line) => line.startsWith("comment "))
    .map((line) => line.slice("comment ".length));
  const hasScan = lines.includes("property ushort scan");
  const stride = POSITION_BYTES + (hasScan ? SCAN_INDEX_BYTES : 0);
  const body = new DataView(buffer, headerLength);
  const count = Math.floor(body.byteLength / stride);
  const positions = new Float32Array(count * 3);
  const scanIndices = hasScan ? new Uint16Array(count) : undefined;
  for (let i = 0; i < count; i++) {
    for (let axis = 0; axis < 3; axis++) {
      positions[i * 3 + axis] = body.getFloat32(i * stride + axis * 4, true);
    }
    if (scanIndices) {
      scanIndices[i] = body.getUint16(i * stride + POSITION_BYTES, true);
    }
  }
  return { positions, scanIndices, comments };
}
